import { makeStyles, tokens } from '@fluentui/react-components';
import type { ReactNode } from 'react';

/**
 * Shared muted-tone status pill. Replaces stock Fluent `Badge`
 * `color="success"|"danger"|"warning"|"important"` / `appearance="filled"`
 * everywhere a status needs to read as part of the palette, not a Christmas
 * light. Pattern lifted from `QueueCallsTab.tsx`'s `OutcomeBadge`
 * (PR #89, branch `claude/queues-tab-rail-counts`).
 *
 * - `positive` / `negative` / `neutral` mirror the Queues Calls tab tones.
 * - `warning` is the visual-system spec's muted yellow warning tone.
 * - `info` is for informational chips (Routed, Transferred, Inbound/Outbound)
 *   that are not a click target -- brand color stays reserved for things you
 *   click (spec section 2.2).
 * - `outline` renders the "neutral outlined badge" routed-state rule: no
 *   fill, border + text only.
 */
export type StatusPillTone = 'positive' | 'negative' | 'warning' | 'neutral' | 'info';

const useStyles = makeStyles({
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  positive: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
  },
  negative: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
  },
  warning: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground1,
  },
  neutral: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  info: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
  },
  outline: {
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground3,
  },
});

export interface StatusPillProps {
  tone: StatusPillTone;
  /** Neutral outlined rendering (spec section 2.2: routed state = neutral outlined). Ignores `tone`'s fill. */
  outline?: boolean;
  children: ReactNode;
  className?: string;
}

export function StatusPill({ tone, outline, children, className }: StatusPillProps) {
  const styles = useStyles();
  const toneClass = outline ? styles.outline : styles[tone];
  return (
    <span className={`${styles.pill} ${toneClass}${className ? ` ${className}` : ''}`}>
      {children}
    </span>
  );
}
